import api from './api';

export type OfferStatus =
  | 'Draft' | 'In Progress' | 'Sent' | 'Accepted' | 'Rejected'
  | 'Closed' | 'Expired' | 'Cancelled';

export interface Offer {
  id: string;
  offerNumber: string;
  status: OfferStatus | string;
  agentId: string;
  agentName?: string;
  subtotal: number;
  vatAmount: number;
  total: number;
  sentAt?: string;
  sendChannel?: string | null;
  sendContact?: string | null;
  createdAt: string;
  updatedAt: string;
  items?: OfferItem[];
  customer?: Customer;
  authorizedPerson?: AuthorizedPerson;
  customerLinkExpiresAt?: string;
  closeNote?: string | null;
  closeReason?: 'Success' | 'Rejected' | 'Lost' | null;
}

export interface OfferItem {
  id: string;
  productId: string;
  locationId?: string;
  status: string;
  price: number;
  quantity?: number;
  contractMonths?: number;
  contactName: string;
  contactMobile: string;
  product?: { id: string; name: string; category: string };
  location?: { lat: number; lng: number; addressLine1?: string; city?: string };
}

export interface Customer {
  id: string;
  nameEn?: string | null;
  nameAr?: string | null;
  crNumber?: string | null;
  crExpiry?: string | null;
  vatNumber?: string | null;
  contactEmail?: string | null;
  contactMobile?: string | null;
  landline?: string | null;
  addressEn?: string | null;
  addressAr?: string | null;
}

export interface AuthorizedPerson {
  id: string;
  nameEn?: string | null;
  nameAr?: string | null;
  mobile?: string | null;
  idType?: string | null;
  idNumber?: string | null;
}

export const offersService = {
  list: () => api.get<Offer[]>('/offers').then((r) => r.data),

  get: (id: string) => api.get<Offer>(`/offers/${id}`).then((r) => r.data),

  create: () => api.post<Offer>('/offers').then((r) => r.data),

  saveCustomerInfo: (offerId: string, data: Partial<Customer>) =>
    api.patch<Offer>(`/offers/${offerId}/customer-info`, data).then((r) => r.data),

  saveAuthorizedPerson: (offerId: string, data: Partial<AuthorizedPerson>) =>
    api.patch<Offer>(`/offers/${offerId}/authorized-person`, data).then((r) => r.data),

  generateLink: (offerId: string, opts?: { channel?: string; contact?: string }) =>
    api
      .post<{ url: string }>(`/offers/${offerId}/customer-link`, opts ?? {})
      .then((r) => r.data),

  getPublic: (offerId: string) =>
    api.get<Offer>(`/offers/${offerId}/public`).then((r) => r.data),

  // Legacy token-based lookup (kept for backwards compatibility)
  getByToken: (token: string) =>
    api.get<Offer>(`/offers/customer-view?token=${token}`).then((r) => r.data),

  accept: (offerId: string, token: string) =>
    api.patch<Offer>(`/offers/${offerId}/accept?token=${token}`).then((r) => r.data),

  reject: (offerId: string, token: string) =>
    api.patch<Offer>(`/offers/${offerId}/reject?token=${token}`).then((r) => r.data),

  uploadDocuments: (offerId: string, docs: {
    customerCR?: File | null;
    customerVAT?: File | null;
    nationalAddress?: File | null;
    authorizedPersonLetter?: File | null;
  }) => {
    const formData = new FormData();
    if (docs.customerCR) formData.append('customerCR', docs.customerCR);
    if (docs.customerVAT) formData.append('customerVAT', docs.customerVAT);
    if (docs.nationalAddress) formData.append('nationalAddress', docs.nationalAddress);
    if (docs.authorizedPersonLetter) formData.append('authorizedPersonLetter', docs.authorizedPersonLetter);
    return api.post(`/offers/${offerId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
};
